import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import React, { useState } from 'react';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import dayjs from 'dayjs';
import { DiscountTypes } from 'api';
import { FormCard, FormInput } from 'components';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
}

const ActiveDatesBlock = (props: Props): JSX.Element | null => {
  const { control, watch, setValue } = props;
  const [showEndDate, setShowEndDate] = useState<boolean>(!!watch('end_date'));

  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const updateStartDateTime = (
    newDate: string | null = null,
    newTime: string | null = null,
  ) => {
    const currentDateTime = startDate ? dayjs(startDate) : null;

    if (!currentDateTime && !newDate) return;

    let updatedDateTime;
    if (!currentDateTime) {
      // If no current value, create from new date
      updatedDateTime = dayjs.utc(newDate).startOf('day');
    } else if (newDate) {
      // Update date portion
      const dateObj = dayjs.utc(newDate);
      updatedDateTime = currentDateTime
        .year(dateObj.year())
        .month(dateObj.month())
        .date(dateObj.date());
    } else if (newTime) {
      // Update time portion
      const timeObj = dayjs(newTime);

      updatedDateTime = currentDateTime
        .hour(timeObj.hour())
        .minute(timeObj.minute())
        .second(timeObj.second());
    }

    setValue('start_date', updatedDateTime?.toISOString() || '');
  };

  const updateEndDateTime = (
    newDate: string | null = null,
    newTime: string | null = null,
  ) => {
    const currentDateTime = endDate ? dayjs(endDate) : null;

    if (!currentDateTime && !newDate) return;

    let updatedDateTime;
    if (!currentDateTime) {
      // If no current value, create from new date and set to end of day
      updatedDateTime = dayjs.utc(newDate).endOf('day');
    } else if (newDate) {
      // Update date portion while preserving time
      const dateObj = dayjs(newDate);
      updatedDateTime = currentDateTime
        .year(dateObj.year())
        .month(dateObj.month())
        .date(dateObj.date());
    } else if (newTime) {
      // Update time portion while preserving date
      const timeObj = dayjs(newTime);

      updatedDateTime = currentDateTime
        .hour(timeObj.hour())
        .minute(timeObj.minute())
        .second(timeObj.second());
    }

    setValue('end_date', updatedDateTime?.toISOString() || null);
  };

  // Handle toggle of end date switch
  const handleEndDateToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setShowEndDate(isChecked);

    if (!isChecked) {
      setValue('end_date', null);
    } else if (!endDate && startDate) {
      const defaultEndDate = dayjs.utc(startDate).endOf('day').toISOString();
      setValue('end_date', defaultEndDate);
    }
  };

  return (
    <FormCard title="Active dates">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormInput
                control={control}
                inputType="datepicker"
                name="start_date"
                label="Start Date"
                tooltip="If start date if after end date, end date field will be cleared"
                rules={{
                  required: true,
                  onChange: (event) => {
                    updateStartDateTime(event.target.value, null);

                    const date = dayjs(event.target.value);

                    // Check if we need to clear end date
                    if (showEndDate && date.isAfter(endDate)) {
                      const start = date.format('DD/MM/YYYY');
                      const end = dayjs(endDate).format('DD/MM/YYYY');
                      if (!dayjs(start).isSame(end)) {
                        setValue('end_date', null);
                      }
                    }
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInput
                control={control}
                inputType="timepicker"
                name="start_date"
                label="Start Time"
                onChange={(time) => {
                  updateStartDateTime(null, time?.toISOString());
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={showEndDate}
                onChange={handleEndDateToggle}
                name="showEndDate"
                color="primary"
              />
            }
            label="Set end date"
          />
        </Grid>
        {showEndDate && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormInput
                  control={control}
                  inputType="datepicker"
                  name="end_date"
                  label="End Date"
                  minDate={dayjs.utc(startDate)}
                  rules={{
                    required: true,
                    onChange: (event) => {
                      const date = event.target.value;

                      updateEndDateTime(date, null);
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormInput
                  control={control}
                  inputType="timepicker"
                  name="end_date"
                  label="End Time"
                  onChange={(time) => {
                    updateEndDateTime(null, time?.toISOString());
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </FormCard>
  );
};

export default ActiveDatesBlock;
